// client/src/components/admin/AdminFeatureFlags.js
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import ApiFeatureFlags from '../../api/ApiFeatureFlags'
import featureFlags, { setFeatureFlags } from '../../config/featureFlags'
import { isOwner } from '../../utils/userRoles'
import toastr from 'toastr'

const FLAG_DESCRIPTIONS = {
  SMS_NOTIFICATIONS: 'Enable SMS notification sending and scheduling.',
  REQUIRE_AVAILABILITY_FOR_PROPOSALS:
    'Require players to set availability before sending proposals.',
  DIRECT_MATCH_PROPOSALS:
    'Allow direct match proposals that bypass availability overlap.',
  EMAIL_OUTBOX_ALL_ADMINS:
    'Allow all admins (not just owners) to access the email outbox.',
  FEATURE_FLAGS_ALL_ADMINS:
    'Allow all admins (not just owners) to access this feature flag panel.',
}

function AdminFeatureFlags() {
  const { user, isAdmin, loading } = useAuth()
  const [flags, setFlags] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [saving, setSaving] = useState({})

  const canAccess =
    isOwner(user) || (isAdmin && featureFlags.FEATURE_FLAGS_ALL_ADMINS)

  const sortedFlags = useMemo(() => {
    return Object.keys(flags).sort((a, b) => a.localeCompare(b))
  }, [flags])

  const fetchFlags = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await ApiFeatureFlags.getAll()
      setFlags(result?.data || {})
      setFeatureFlags(result?.data || {})
    } catch (error) {
      console.error('Failed to load feature flags:', error)
      toastr.error('Failed to load feature flags')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin && canAccess) {
      fetchFlags()
    }
  }, [isAdmin, canAccess, fetchFlags])

  const handleToggle = async (flagName) => {
    const nextValue = !flags[flagName]
    const action = nextValue ? 'enable' : 'disable'

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${flagName}?\n\nThis will take effect immediately across the application.`,
    )

    if (!confirmed) {
      return
    }

    setSaving((prev) => ({ ...prev, [flagName]: true }))
    try {
      const result = await ApiFeatureFlags.update({
        [flagName]: nextValue,
      })
      setFlags(result?.data?.flags || {})
      setFeatureFlags(result?.data?.flags || {})
      toastr.success(`Updated ${flagName}`)
    } catch (error) {
      console.error('Failed to update feature flag:', error)
      toastr.error(`Failed to update ${flagName}`)
    } finally {
      setSaving((prev) => ({ ...prev, [flagName]: false }))
    }
  }

  if (loading) {
    return (
      <PageLayout title="Feature Flags">
        <div className="container py-4">
          <p>Loading...</p>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin || !canAccess) {
    return (
      <PageLayout title="Feature Flags">
        <div className="container py-4">
          <p>Access denied. Owner access required.</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Feature Flags">
      <div className="container py-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h2 className="h4 mb-1">Feature Flags</h2>
            <p className="text-muted mb-0">
              View the current state of all runtime feature flags.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={fetchFlags}
            disabled={isLoading}
            data-testid="feature-flags-refresh"
          >
            Refresh
          </button>
        </div>

        <div className="alert alert-warning" role="alert">
          <strong>⚠️ Important:</strong> Changes apply immediately but are{' '}
          <strong>not persisted</strong> across server restarts.
          <br />
          <strong>To persist changes:</strong>
          <ul className="mb-0 mt-2">
            <li>
              <strong>Local:</strong> Add <code>FEATURE_FLAG_NAME=true</code> to
              your <code>.env</code> file
            </li>
            <li>
              <strong>Production:</strong> Run{' '}
              <code>eb setenv FEATURE_FLAG_NAME=true -e environment-name</code>
            </li>
          </ul>
          See the{' '}
          <a
            href="https://github.com/hkauffman3/flex-league/blob/develop/docs/feature-flags.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            Feature Flags documentation
          </a>{' '}
          for details.
        </div>

        <div className="card shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Flag</th>
                    <th scope="col">Description</th>
                    <th scope="col" className="text-center">
                      Status
                    </th>
                    <th scope="col" className="text-center">
                      Toggle
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Loading feature flags...
                      </td>
                    </tr>
                  ) : (
                    sortedFlags.map((flagName) => {
                      const isEnabled = Boolean(flags[flagName])
                      const isSaving = Boolean(saving[flagName])
                      return (
                        <tr key={flagName}>
                          <td>
                            <span className="fw-semibold">{flagName}</span>
                          </td>
                          <td className="text-muted">
                            {FLAG_DESCRIPTIONS[flagName] ||
                              'No description provided.'}
                          </td>
                          <td className="text-center">
                            <span
                              className={`badge ${
                                isEnabled ? 'bg-success' : 'bg-secondary'
                              }`}
                            >
                              {isEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="form-check form-switch d-inline-flex align-items-center gap-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`flag-${flagName}`}
                                checked={isEnabled}
                                onChange={() => handleToggle(flagName)}
                                disabled={isSaving}
                                data-testid={`feature-flag-toggle-${flagName}`}
                              />
                              {isSaving ? (
                                <span className="small text-muted">
                                  Saving...
                                </span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                  {!isLoading && sortedFlags.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        No feature flags found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminFeatureFlags
